
// src/wiring/installCapabilities.ts
//
// installCapabilities (v1)
// ------------------------
// Deterministic install pass:
// - Computes installed set (provided as descriptors)
// - Emits ops capability events (installed + optional uninstall + heartbeat)
// - Emits wiring_ready with manifest checksum + manifestSummary (count-only)
// - Optionally emits full wiring manifest (still witness-safe) for UI breakdown
//
// Best-practice locks:
// - manifestSummary is count-only (safe for identity + embeds)
// - full manifest is optional and can omit labels to avoid sensitive name leakage
// - node ids are normalized + hashed (stable, opaque) by default
// - deterministic ordering everywhere (sort by stable ids)
// - SSR-safe (no window => no-op)
//

import {
  SYNK_WIRING_READY_EVENT,
  SYNK_OPS_CAPABILITY_EVENT,
  SYNK_OPS_TIMELINE_EVENT,
  type SynkWiringReadyDetailV1,
  type SynkOpsCapabilityDetailV1,
} from "@/contracts/ops/OpsEvents";

import type { CapabilityId, OpsHealth } from "@/contracts/ops/OpsDashboardModel";
import type {
  SynkWiringManifestV1,
  SynkWiringManifestSummaryV1,
  SynkWiringNodeV1,
  SynkWiringNodeKindV1,
} from "@/contracts/ops/WiringManifest";

export type CapabilityDescriptorV1 = {
  id: CapabilityId;
  tierRequired: "public" | "creator" | "enterprise" | "regulated";
  safety: "witness" | "authority" | "transformer";
  requires?: CapabilityId[];
};

export type InstallContextV1 = {
  buildId: string;
  env: "prod" | "staging" | "dev" | "unknown";
  tier: "public" | "creator" | "enterprise" | "regulated" | "unknown";
  authoritySource: { kind: "user" | "service" | "unknown"; id: string };
};

export type InstallCapabilitiesOptsV1 = {
  /**
   * If true, include full manifest on wiring_ready.
   * Recommended: keep OFF by default; UI can request/derive breakdown in-process.
   */
  emitFullManifest?: boolean;

  /**
   * If true, include node.label fields (may leak sensitive names).
   * Recommended: keep OFF; keep ids hashed/opaque.
   */
  includeLabels?: boolean;

  /**
   * Heartbeat interval (ms). Default 10s. Minimum 1000ms.
   */
  heartbeatMs?: number;

  /**
   * Optional extra wiring nodes (bridges, runtime checks, exporters) that are
   * not represented as "capability descriptors". Must be deterministic ids.
   */
  extraNodes?: SynkWiringNodeV1[];

  /**
   * Optional route summary to attach to manifest (safe).
   * Provide only stable route ids and public paths.
   */
  routes?: Array<{ path: string; id: string }>;

  /**
   * Optional callback to report current per-capability health on heartbeat.
   * If omitted, heartbeat emits status "ok".
   */
  getHeartbeatStatus?: (capId: CapabilityId) => { status: OpsHealth; reason?: string } | null;
};

/* ------------------------------------------------------------------ */
/* Hash + normalization                                                 */
/* ------------------------------------------------------------------ */

/** Simple deterministic 32-bit FNV-1a hash (hex) */
function fnv1a32(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ("00000000" + h.toString(16)).slice(-8);
}

/**
 * Normalize an opaque id to a stable, non-sensitive node id.
 * We intentionally hash the original id to avoid leaking internal names.
 */
function toOpaqueNodeId(prefix: string, rawId: string): string {
  // Keep prefix stable and human-auditable; hash the rest.
  return `${prefix}.${fnv1a32(String(rawId))}`;
}

/** Deterministic kind inference from id prefix (stable contract rule). */
function inferKindFromId(id: string): SynkWiringNodeKindV1 {
  // Stable prefix rules. If you introduce new conventions, extend here.
  if (id.startsWith("route.")) return "route";
  if (id.startsWith("provider.")) return "provider";
  if (id.startsWith("bridge.")) return "bridge";
  if (id.startsWith("check.")) return "runtime_check";
  if (id.startsWith("exporter.")) return "exporter";
  // Fallback: treat unknown as runtime_check (conservative)
  return "runtime_check";
}

/* ------------------------------------------------------------------ */
/* Manifest build + summary                                             */
/* ------------------------------------------------------------------ */

function summarizeManifest(manifest: SynkWiringManifestV1): SynkWiringManifestSummaryV1 {
  const nodes = manifest.nodes ?? [];
  const providers = nodes.filter((n) => n.kind === "provider").length;
  const bridges = nodes.filter((n) => n.kind === "bridge").length;
  const checks = nodes.filter((n) => n.kind === "runtime_check").length;
  const routes = (manifest.routes ?? []).length;

  return { v: 1, nodes: nodes.length, routes, providers, bridges, checks };
}

function buildManifest(
  ctx: InstallContextV1,
  caps: CapabilityDescriptorV1[],
  opts?: InstallCapabilitiesOptsV1,
): SynkWiringManifestV1 {
  const includeLabels = opts?.includeLabels === true;

  // Deterministic ordering by stable opaque id
  const orderedCaps = [...caps].sort((a, b) => String(a.id).localeCompare(String(b.id)));

  const capNodes: SynkWiringNodeV1[] = orderedCaps.map((c) => {
    // Capabilities are installed wiring providers (in the broad sense).
    const id = toOpaqueNodeId("provider.cap", c.id);

    return {
      v: 1,
      kind: "provider",
      id,
      label: includeLabels ? String(c.id) : undefined, // OFF by default
      safety: c.safety,
      requires: (c.requires ?? []).map((r) => toOpaqueNodeId("provider.cap", r)),
      status: "unknown",
    };
  });

  // Normalize/ensure determinism for any extra nodes
  const extra = (opts?.extraNodes ?? [])
    .map((n) => {
      const kind = n.kind ?? inferKindFromId(n.id);
      return {
        ...n,
        v: 1,
        kind,
      } as SynkWiringNodeV1;
    })
    .sort((a, b) => a.id.localeCompare(b.id));

  // Merge and keep deterministic
  const nodes = [...capNodes, ...extra].sort((a, b) => a.id.localeCompare(b.id));

  // Routes are safe summaries; keep deterministic ordering
  const routes = (opts?.routes ?? []).slice().sort((a, b) => {
    const ap = a.path.localeCompare(b.path);
    return ap !== 0 ? ap : a.id.localeCompare(b.id);
  });

  return {
    v: 1,
    at: Date.now(),
    buildId: ctx.buildId,
    env: ctx.env,
    tier: ctx.tier,
    nodes,
    routes,
  };
}

/**
 * Stable checksum input: must NOT include timestamps, labels, or mutable fields.
 * Keep it strictly structural.
 */
function computeManifestChecksum(manifest: SynkWiringManifestV1): string {
  const stable = {
    v: manifest.v,
    buildId: manifest.buildId,
    env: manifest.env,
    tier: manifest.tier,
    routes: (manifest.routes ?? []).map((r) => ({ path: r.path, id: r.id })),
    nodes: (manifest.nodes ?? []).map((n) => ({
      kind: n.kind,
      id: n.id,
      safety: n.safety,
      requires: n.requires ?? [],
    })),
  };

  return fnv1a32(JSON.stringify(stable));
}

/* ------------------------------------------------------------------ */
/* Emit helpers                                                        */
/* ------------------------------------------------------------------ */

function safeDispatch(eventName: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch {
    // never throw
  }
}

/* ------------------------------------------------------------------ */
/* Public install                                                      */
/* ------------------------------------------------------------------ */

export function installCapabilities(
  ctx: InstallContextV1,
  installed: CapabilityDescriptorV1[],
  opts?: InstallCapabilitiesOptsV1,
): () => void {
  // SSR-safe
  if (typeof window === "undefined") return () => {};

  const at = Date.now();

  // Build wiring manifest (deterministic)
  const manifest = buildManifest(ctx, installed, opts);
  const summary = summarizeManifest(manifest);
  const checksum = computeManifestChecksum(manifest);

  // Emit capability "installed" (one per descriptor)
  for (const c of installed) {
    const detail: SynkOpsCapabilityDetailV1 = {
      v: 1,
      at,
      kind: "installed",
      id: c.id,
      tierRequired: c.tierRequired,
      safety: c.safety,
      status: "ok",
      reason: "install_pass",
    };
    safeDispatch(SYNK_OPS_CAPABILITY_EVENT, detail);
  }

  // Emit wiring ready (anchor event)
  const ready: SynkWiringReadyDetailV1 = {
    v: 1,
    at,
    buildId: ctx.buildId,
    manifestChecksum: checksum,
    env: ctx.env,
    tier: ctx.tier,
    authoritySource: ctx.authoritySource,
    installed: installed.map((c) => ({
      id: c.id,
      tierRequired: c.tierRequired,
      safety: c.safety,
    })),

    // Best-practice: count-only summary is always safe for identity/contract.
    manifestSummary: summary,

    // Full manifest is optional; keep labels off by default to avoid leakage.
    manifest: opts?.emitFullManifest ? manifest : undefined,
  };

  safeDispatch(SYNK_WIRING_READY_EVENT, ready);

  // Timeline evidence (witness-safe)
  safeDispatch(SYNK_OPS_TIMELINE_EVENT, {
    v: 1,
    at,
    kind: "policy_decision",
    detail: {
      buildId: ctx.buildId,
      tier: ctx.tier,
      manifest: checksum,
      nodes: summary.nodes,
      providers: summary.providers,
      bridges: summary.bridges,
      routes: summary.routes,
      checks: summary.checks,
    },
  });

  // Heartbeat (capability-level) — witness-safe
  const ms = Math.max(1000, Number(opts?.heartbeatMs ?? 10_000));

  const timer = window.setInterval(() => {
    const now = Date.now();
    for (const c of installed) {
      const hbStatus = opts?.getHeartbeatStatus?.(c.id) ?? null;

      const hb: SynkOpsCapabilityDetailV1 = {
        v: 1,
        at: now,
        kind: "heartbeat",
        id: c.id,
        tierRequired: c.tierRequired,
        safety: c.safety,
        status: hbStatus?.status ?? "ok",
        reason: hbStatus?.reason,
      };

      safeDispatch(SYNK_OPS_CAPABILITY_EVENT, hb);
    }
  }, ms);

  return () => {
    try {
      window.clearInterval(timer);
    } catch {}

    // Optional: emit "uninstalled" on teardown (useful in dev/hot reload)
    const endAt = Date.now();
    for (const c of installed) {
      const detail: SynkOpsCapabilityDetailV1 = {
        v: 1,
        at: endAt,
        kind: "uninstalled",
        id: c.id,
        tierRequired: c.tierRequired,
        safety: c.safety,
        status: "unknown",
        reason: "uninstall_pass",
      };
      safeDispatch(SYNK_OPS_CAPABILITY_EVENT, detail);
    }
  };
}
